import type React from "react";
import {useRef} from "react";

import {useFrame} from "@react-three/fiber";
import {Matrix4, type Mesh, Vector3} from "three";

import {useSelection} from "../useSelection";
import {DraggableTransform} from "./drag/DraggableTransform";
import {useButtonScale} from "./useButtonScale";

interface ScaleButtonProps {
  relativePosition: [number, number]; // Relative position [-1, -1] to [1, 1]
  constrainAxis?: "x" | "y" | "both"; // Axis constraint
}

// Pre-allocated objects for transformation computations
const scaleHandlePos = new Vector3();
const scaleMirroredPos = new Vector3();
const scaleNewHandlePos = new Vector3();

const scaleHandlePos2 = new Vector3();
const scaleMirroredPos2 = new Vector3();
const scaleNewHandlePos2 = new Vector3();

const matToBoxCenter = new Matrix4();
const matRotateBox = new Matrix4();

const scaleToMirroredPos = new Matrix4();
const scaleRotationM = new Matrix4();
const scaleMergedMatrix = new Matrix4();
const scaleMergedInvMatrix = new Matrix4();
const scaleMatrix = new Matrix4();

const scaleButtonPos = new Vector3();

const ScaleButton: React.FC<ScaleButtonProps> = ({
  relativePosition,
  constrainAxis = "both",
}) => {
  const {buttonScale} = useButtonScale();
  const {selectionBoundingBox, applyTransformToSelected} = useSelection();
  const buttonRef = useRef<Mesh>(null);

  const handleScaleTransform = (deltaMatrix: Matrix4) => {
    if (!selectionBoundingBox.current) return;

    // Set up positions (in local space with z = 50)
    scaleHandlePos.set(relativePosition[0] / 2, relativePosition[1] / 2, 50);
    scaleMirroredPos.set(
      -relativePosition[0] / 2,
      -relativePosition[1] / 2,
      50,
    );

    // Convert to world coordinates using the selection's transformation
    selectionBoundingBox.current.localToWorld(scaleHandlePos);
    selectionBoundingBox.current.localToWorld(scaleMirroredPos);

    // Get the new handle position by applying the delta transformation
    scaleNewHandlePos.copy(scaleHandlePos).applyMatrix4(deltaMatrix);

    // Transform these positions into the bounding box's local space.
    // First, create a translation matrix that recenters at the selection center.
    matToBoxCenter
      .makeTranslation(
        selectionBoundingBox.current.position.x,
        selectionBoundingBox.current.position.y,
        selectionBoundingBox.current.position.z,
      )
      .invert();
    // Next, invert the selection's rotation.
    matRotateBox.makeRotationFromEuler(selectionBoundingBox.current.rotation);
    matRotateBox.invert();

    scaleHandlePos2
      .copy(scaleHandlePos)
      .applyMatrix4(matToBoxCenter)
      .applyMatrix4(matRotateBox);
    scaleMirroredPos2
      .copy(scaleMirroredPos)
      .applyMatrix4(matToBoxCenter)
      .applyMatrix4(matRotateBox);
    scaleNewHandlePos2
      .copy(scaleNewHandlePos)
      .applyMatrix4(matToBoxCenter)
      .applyMatrix4(matRotateBox);

    // Compute scaling factors for x and y separately.
    const deltaX = Math.abs(scaleNewHandlePos2.x - scaleMirroredPos2.x);
    const originalDeltaX = Math.abs(scaleHandlePos2.x - scaleMirroredPos2.x);
    const scaleFactorX = deltaX / originalDeltaX;

    const deltaY = Math.abs(scaleNewHandlePos2.y - scaleMirroredPos2.y);
    const originalDeltaY = Math.abs(scaleHandlePos2.y - scaleMirroredPos2.y);
    const scaleFactorY = deltaY / originalDeltaY;

    let appliedScaleFactorX = scaleFactorX;
    let appliedScaleFactorY = scaleFactorY;

    // Constrain axis scaling if specified
    if (constrainAxis === "x") {
      appliedScaleFactorY = 1;
    } else if (constrainAxis === "y") {
      appliedScaleFactorX = 1;
    } else {
      const avg = (appliedScaleFactorX + appliedScaleFactorY) / 2;
      appliedScaleFactorX = avg;
      appliedScaleFactorY = avg;
    }

    // Now create a transformation that scales relative to the mirrored corner.
    scaleToMirroredPos
      .makeTranslation(
        scaleMirroredPos.x,
        scaleMirroredPos.y,
        scaleMirroredPos.z,
      )
      .invert();
    scaleRotationM.makeRotationFromEuler(selectionBoundingBox.current.rotation);
    scaleRotationM.invert();

    scaleMergedMatrix.copy(scaleRotationM).multiply(scaleToMirroredPos);
    scaleMatrix.makeScale(appliedScaleFactorX, appliedScaleFactorY, 1);
    scaleMergedInvMatrix.copy(scaleMergedMatrix).invert();

    const transformations = [
      scaleMergedMatrix,
      scaleMatrix,
      scaleMergedInvMatrix,
    ];
    applyTransformToSelected(transformations);
  };

  useFrame(() => {
    if (buttonRef.current) {
      if (!selectionBoundingBox.current) {
        // Hide the scale button when there’s no selection
        buttonRef.current.scale.set(0, 0, 0);
      } else {
        // Position the button relative to the selection
        scaleButtonPos.set(relativePosition[0] / 2, relativePosition[1] / 2, 0);
        selectionBoundingBox.current.localToWorld(scaleButtonPos);
        scaleButtonPos.z = 50;
        buttonRef.current.position.copy(scaleButtonPos);

        buttonRef.current.scale.set(
          buttonScale.current,
          buttonScale.current,
          1,
        );
        buttonRef.current.rotation.copy(selectionBoundingBox.current.rotation);
      }
    }
  });

  return (
    <DraggableTransform onTransform={handleScaleTransform}>
      <mesh ref={buttonRef}>
        <planeGeometry args={[1.1, 1.1]} />
        <meshBasicMaterial color="black" />
        <mesh>
          <planeGeometry args={[1, 1]} />
          <meshBasicMaterial color="lightgray" />
        </mesh>
      </mesh>
    </DraggableTransform>
  );
};

export default ScaleButton;
